<script lang="ts">
  import { onMount } from 'svelte';
  import type { ClientUserSummary, TenantSummary } from '@ucc/shared';
  import {
    ApiError,
    createClientUser,
    fetchClientUsers,
    fetchTenants,
    resetClientPassword,
    updateClientUser,
    updateTenant,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let tenant = $state<TenantSummary | null>(null);
  let users = $state<ClientUserSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let nameError = $state<string | null>(null);
  let nameOk = $state<string | null>(null);
  let savingName = $state(false);
  let editName = $state('');

  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);
  let creating = $state(false);
  let busyId = $state<string | null>(null);

  let email = $state('');
  let displayName = $state('');

  const meId = $derived(session.user?.id ?? '');
  const atLimit = $derived(tenant ? users.length >= tenant.clientUserLimit : false);

  function fmtWhen(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [uRes, tRes] = await Promise.all([fetchClientUsers(), fetchTenants()]);
      users = uRes.users;
      tenant = tRes.tenants[0] ?? null;
      editName = tenant?.name ?? '';
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load organization';
    } finally {
      loading = false;
    }
  }

  async function onRename(e: Event) {
    e.preventDefault();
    nameError = null;
    nameOk = null;
    const name = editName.trim();
    if (!tenant) return;
    if (!name) {
      nameError = 'Organization name is required.';
      return;
    }
    if (name === tenant.name) return;
    savingName = true;
    try {
      const res = await updateTenant(tenant.id, { name });
      tenant = res.tenant;
      nameOk = 'Organization name updated.';
    } catch (err) {
      nameError = err instanceof ApiError ? err.message : 'Rename failed';
    } finally {
      savingName = false;
    }
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    actionError = null;
    actionOk = null;
    const em = email.trim().toLowerCase();
    const name = displayName.trim();
    if (!em || !name) {
      actionError = 'Email and display name are required.';
      return;
    }
    if (!tenant) return;
    creating = true;
    try {
      const res = await createClientUser({ email: em, displayName: name, tenantId: tenant.id });
      users = [...users, res.user].sort((a, b) => a.email.localeCompare(b.email));
      actionOk = res.emailSent
        ? `Invite sent to ${res.user.email}. They must activate within 72 hours.`
        : `Account created; invite email could not be sent${res.emailError ? ` (${res.emailError})` : ''}.`;
      email = '';
      displayName = '';
      const tRes = await fetchTenants();
      tenant = tRes.tenants[0] ?? tenant;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      creating = false;
    }
  }

  async function onToggle(u: ClientUserSummary) {
    actionError = null;
    actionOk = null;
    const wantDisabled = !u.disabledAt;
    busyId = u.id;
    try {
      const res = await updateClientUser(u.id, { disabled: wantDisabled });
      users = users.map((x) => (x.id === u.id ? res.user : x));
      actionOk = wantDisabled ? `Disabled ${u.email}` : `Enabled ${u.email}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Update failed';
    } finally {
      busyId = null;
    }
  }

  async function onResetPassword(u: ClientUserSummary) {
    if (u.disabledAt) {
      actionError = 'Enable the account before sending a reset link.';
      return;
    }
    if (
      !window.confirm(
        u.activatedAt
          ? `Send password reset email to ${u.email}?`
          : `Resend activation invite to ${u.email}?`,
      )
    ) {
      return;
    }
    actionError = null;
    actionOk = null;
    busyId = u.id;
    try {
      const res = await resetClientPassword(u.id);
      actionOk = res.emailSent
        ? u.activatedAt
          ? `Reset email sent to ${u.email}`
          : `Invite re-sent to ${u.email}`
        : `Link created but email failed${res.emailError ? ` (${res.emailError})` : ''}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to send reset email';
    } finally {
      busyId = null;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Organization</h2>
  <p class="sub">Your organization name and team accounts. Any active member can manage the others.</p>

  {#if error}
    <Card><p class="err" role="alert">{error}</p></Card>
  {:else if loading}
    <Card><p class="muted">Loading…</p></Card>
  {:else if tenant}
    <Card title="Organization name">
      <form class="rename" onsubmit={(e) => void onRename(e)}>
        <input class="input" type="text" maxlength={120} bind:value={editName} disabled={savingName} required />
        <Button type="submit" disabled={savingName || editName.trim() === tenant.name}>
          {savingName ? 'Saving…' : 'Rename'}
        </Button>
      </form>
      {#if nameError}
        <p class="err" role="alert">{nameError}</p>
      {/if}
      {#if nameOk}
        <p class="ok" role="status">{nameOk}</p>
      {/if}
    </Card>

    <Card
      title="Create team member"
      subtitle={`Sends 72-hour activation invite · ${users.length} of ${tenant.clientUserLimit} used`}
    >
      {#if atLimit}
        <p class="muted">
          This organization has reached its limit of {tenant.clientUserLimit} team accounts. Disable one
          before adding another.
        </p>
      {:else}
        <form class="create" onsubmit={(e) => void onCreate(e)}>
          <label class="field">
            <span>Email</span>
            <input type="email" bind:value={email} autocomplete="off" disabled={creating} required />
          </label>
          <label class="field">
            <span>Display name</span>
            <input type="text" bind:value={displayName} autocomplete="off" disabled={creating} required />
          </label>
          <div class="form-actions">
            <Button type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create'}</Button>
          </div>
        </form>
      {/if}
      {#if actionError}
        <p class="err" role="alert">{actionError}</p>
      {/if}
      {#if actionOk}
        <p class="ok" role="status">{actionOk}</p>
      {/if}
    </Card>

    <Card title="Team members" subtitle={`${users.length} account(s)`} padded={false}>
      {#if users.length === 0}
        <p class="banner muted">No team members yet.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each users as u (u.id)}
                <tr class:disabled-row={!!u.disabledAt}>
                  <td class="mono">{u.email}</td>
                  <td>{u.displayName}</td>
                  <td>
                    {#if u.disabledAt}
                      <Badge tone="danger">Disabled</Badge>
                    {:else if !u.activatedAt}
                      <Badge tone="warning">Pending</Badge>
                    {:else}
                      <Badge tone="success">Active</Badge>
                    {/if}
                  </td>
                  <td class="muted">{fmtWhen(u.createdAt)}</td>
                  <td class="row-actions">
                    {#if u.id !== meId}
                      <Button
                        type="button"
                        size="sm"
                        variant={u.disabledAt ? 'secondary' : 'danger'}
                        disabled={busyId === u.id}
                        onclick={() => void onToggle(u)}
                      >
                        {#if busyId === u.id}…{:else if u.disabledAt}Enable{:else}Disable{/if}
                      </Button>
                      {#if !u.disabledAt}
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={busyId === u.id}
                          onclick={() => void onResetPassword(u)}
                        >
                          {#if busyId === u.id}…{:else if u.activatedAt}Reset password{:else}Resend invite{/if}
                        </Button>
                      {/if}
                    {:else}
                      <span class="muted">You</span>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .rename {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
    flex: 1 1 16rem;
    min-width: 0;
  }
  .create {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.75rem 1rem;
    align-items: end;
  }
  .form-actions {
    display: flex;
    align-items: end;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
  }
  .field span {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .field input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .err {
    color: #f87171;
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .ok {
    color: var(--success, #22c55e);
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .banner.muted {
    padding: 1.25rem 1rem;
    margin: 0;
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: middle;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.85rem;
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
    display: flex;
    gap: 0.4rem;
    justify-content: flex-end;
  }
  tr.disabled-row td {
    opacity: 0.72;
  }
</style>
